import { useState, useEffect, useRef } from 'react';
import { useParams, Outlet, Link } from 'react-router-dom';
import { onSnapshot } from 'firebase/firestore';
import { projectDoc } from '../constants/firestorePaths';
import WorkflowLayout from '../components/WorkflowLayout';
import RoleAwareBanner from '../components/permissions/RoleAwareBanner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isPermissionDenied, projectLoadState, shouldRecheckOrg } from '../features/orgs/orgAccess';

interface Project {
  name: string;
  /**
   * Required. Enforced at creation by createProjectV2 and never derived —
   * it is stamped on every deliverable filename. Declared required here so
   * this file agrees with functions/src/models/types.ts; a project that
   * predates enforcement reads as an empty string, which shows as missing
   * rather than as a plausible-looking default.
   */
  shortName: string;
  description?: string;
  phase?: string;
  studyType?: string;
  protocolFilePath?: string;
}

export default function ProjectWrapper() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  // Why a project could not be shown. "Project not found" used to cover all
  // three, including an account whose sign-in carried no organisation, which
  // was told a study that existed did not (see features/orgs/orgAccess.ts).
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [orgCheck, setOrgCheck] = useState<'pending' | 'has' | 'none'>('pending');
  const [attempt, setAttempt] = useState(0);
  const recheckedRef = useRef(false);

  useEffect(() => {
    if (!projectId) return;
    // A new id, or Check again, starts from a clean slate: without resetting
    // `loading`, a second failing project would leave the org check unrun and
    // the spinner up for good.
    setLoading(true);
    setOrgCheck('pending');

    const unsubProject = onSnapshot(
      projectDoc(projectId),
      (snapshot) => {
        setProject(snapshot.exists() ? (snapshot.data() as Project) : null);
        setPermissionDenied(false);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading project:', error);
        // Never leave the previous project's data under this project's URL.
        setProject(null);
        setPermissionDenied(isPermissionDenied(error));
        setLoading(false);
      }
    );

    return () => {
      unsubProject();
    };
  }, [projectId, attempt]);

  // Only when the project could not be shown: does this sign-in carry an
  // organisation? If not, refresh the sign-in once; an account that gained
  // its organisation after signing in then loads without signing out.
  useEffect(() => {
    if (loading || project || !user) return;
    let cancelled = false;
    (async () => {
      let orgId = (await user.getIdTokenResult()).claims.orgId;
      if (
        typeof orgId !== 'string' &&
        shouldRecheckOrg({ orgMissing: true, alreadyRechecked: recheckedRef.current })
      ) {
        recheckedRef.current = true;
        orgId = (await user.getIdTokenResult(true)).claims.orgId;
        if (typeof orgId === 'string') {
          if (!cancelled) {
            setLoading(true);
            setAttempt((a) => a + 1);
          }
          return;
        }
      }
      if (!cancelled) setOrgCheck(typeof orgId === 'string' ? 'has' : 'none');
    })().catch((err) => {
      console.error('Error checking organisation:', err);
      if (!cancelled) setOrgCheck('has');
    });
    return () => {
      cancelled = true;
    };
  }, [loading, project, user, attempt]);

  if (loading || (!project && orgCheck === 'pending')) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    const state = projectLoadState({
      exists: false,
      permissionDenied,
      hasOrg: orgCheck === 'has',
    });
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <p className="text-gray-900 font-medium">{state.title}</p>
          <p className="text-gray-500 text-sm mt-1">{state.body}</p>
          <div className="mt-4 flex items-center justify-center gap-4 text-sm">
            {state.kind === 'no-organisation' && (
              <button
                onClick={() => {
                  recheckedRef.current = false;
                  setLoading(true);
                  setAttempt((a) => a + 1);
                }}
                className="rounded-lg bg-gray-900 px-4 py-2 font-medium text-white hover:bg-gray-800"
              >
                Check again
              </button>
            )}
            <Link to="/" className="text-primary-600 hover:underline">
              Back to your studies
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <WorkflowLayout
      projectId={projectId!}
      projectName={project.name || project.shortName || 'Untitled Project'}
    >
      <RoleAwareBanner projectId={projectId!} />
      <Outlet />
    </WorkflowLayout>
  );
}
